import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { Account } from '@/shared/api/protocol-ts/api/billing_new/dto_account_new_pb';
import {
  DiagnocoinBatch,
  DiagnocoinPriceList,
  DiagnocoinWallet,
  ReferralRewards,
} from '@/shared/api/protocol-ts/api/billing_new/dto_diagnocoin_new_pb';
import { Order } from '@/shared/api/protocol-ts/api/billing_new/dto_order_new_pb';
import {
  ProductModel,
  ProductState,
} from '@/shared/api/protocol-ts/api/billing_new/dto_product_new_pb';
import { LoadingStateType, SliceName } from '@/shared/config';

import { isNotOlderRevision } from '../lib';

import {
  getAvailableProducts,
  setAccountSettings,
} from './billingSlice.thunks';

const initialState = {
  account: {} as Account,
  inventory: [] as ProductState[],
  invoices: [] as Order[],
  availableProducts: [] as ProductModel[],
  subscriptionLifeTime: 1,
  hasOverdueSubscription: false,
  CBCTConsumption: { limit: 0, leftover: 0 },
  orderIDForPayment: '',
  loading: 'idle' as LoadingStateType,
  diagnocoinWallet: {} as DiagnocoinWallet,
  diagnocoinBatches: [] as DiagnocoinBatch[],
  diagnocoinPrices: {} as DiagnocoinPriceList,
  diagnocoinLoading: 'idle' as LoadingStateType,
  referralRewards: null as ReferralRewards | null,
};

const billingSlice = createSlice({
  name: SliceName.billing,
  initialState,
  reducers: {
    setAccount: (state, { payload }: PayloadAction<Account>) => {
      state.account = payload;
    },
    setInventory: (state, { payload }: PayloadAction<ProductState[]>) => {
      state.inventory = payload;
    },
    setInvoices: (state, action: PayloadAction<Order[]>) => {
      state.invoices = action.payload.reverse();
    },
    setAvailableProducts: (
      state,
      { payload }: PayloadAction<ProductModel[]>,
    ) => {
      state.availableProducts = payload.filter(
        (product) =>
          product.Kind.case === 'Subscription' &&
          !product.AvailableForAdminsOnly,
      );
    },
    setSubscriptionLifeTime: (state, { payload }: PayloadAction<number>) => {
      state.subscriptionLifeTime = payload;
    },
    updateInventory: (state, { payload }: PayloadAction<ProductState>) => {
      const inventoryForUpdateIndex = state.inventory.findIndex(
        (inventoryItem) => inventoryItem.ID === payload.ID,
      );

      if (inventoryForUpdateIndex < 0) {
        state.inventory = [payload, ...state.inventory];
      } else {
        state.inventory[inventoryForUpdateIndex] = payload;
      }
    },
    updateOrders: (state, action: PayloadAction<Order>) => {
      const orderForUpdateIndex = state.invoices.findIndex(
        (invoice) => invoice.ID === action.payload.ID,
      );

      if (orderForUpdateIndex < 0) {
        state.invoices = [action.payload, ...state.invoices];
      } else {
        state.invoices[orderForUpdateIndex] = action.payload;
      }
    },
    setHasOverdueSubscription: (state, action: PayloadAction<boolean>) => {
      state.hasOverdueSubscription = action.payload;
    },
    setOrderIDForPayment: (state, { payload }: PayloadAction<string>) => {
      state.orderIDForPayment = payload;
    },
    setLoading: (state, action: PayloadAction<LoadingStateType>) => {
      state.loading = action.payload;
    },
    setDiagnocoinWallet: (
      state,
      { payload }: PayloadAction<DiagnocoinWallet>,
    ) => {
      state.diagnocoinWallet = payload;
    },
    setDiagnocoinPrices: (
      state,
      { payload }: PayloadAction<DiagnocoinPriceList>,
    ) => {
      state.diagnocoinPrices = payload;
    },
    setDiagnocoinLoading: (
      state,
      { payload }: PayloadAction<LoadingStateType>,
    ) => {
      state.diagnocoinLoading = payload;
    },
    setDiagnocoinBatches: (
      state,
      { payload }: PayloadAction<DiagnocoinBatch[]>,
    ) => {
      const liveBatches = Object.fromEntries(
        state.diagnocoinBatches.map((batch) => [batch.ID, batch]),
      );

      state.diagnocoinBatches = payload.map((historical) => {
        const live = liveBatches[historical.ID];

        return live && isNotOlderRevision(live, historical) ? live : historical;
      });
    },
    updateDiagnocoinBatch: (
      state,
      { payload }: PayloadAction<DiagnocoinBatch>,
    ) => {
      const batchForUpdateIndex = state.diagnocoinBatches.findIndex(
        (batch) => batch.ID === payload.ID,
      );

      if (batchForUpdateIndex < 0) {
        state.diagnocoinBatches = [payload, ...state.diagnocoinBatches];

        return;
      }

      if (
        isNotOlderRevision(
          payload,
          state.diagnocoinBatches[batchForUpdateIndex],
        )
      ) {
        state.diagnocoinBatches[batchForUpdateIndex] = payload;
      }
    },
    setReferralRewards: (
      state,
      { payload }: PayloadAction<ReferralRewards>,
    ) => {
      state.referralRewards = payload;
    },
    resetDiagnocoins: (state) => {
      state.diagnocoinWallet = initialState.diagnocoinWallet;
      state.diagnocoinBatches = [];
      state.referralRewards = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(setAccountSettings.fulfilled, (state, { payload }) => {
      if (payload.Account) {
        state.account = payload.Account;
      }
    });

    builder.addCase(getAvailableProducts.fulfilled, (state, { payload }) => {
      if (payload.AvailableProducts?.length > 0) {
        state.availableProducts = payload.AvailableProducts;
      }
    });
  },
});

export const { actions } = billingSlice;

export default billingSlice.reducer;
